import { auth } from '@/lib/firebase';
import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { toast } from 'sonner';
import { getAtomsForTier } from './atoms';

export const useDeleteInstance = (tier: string) => {
  const atoms = getAtomsForTier(tier);
  const setInstance = useSetAtom(atoms.instanceAtom);
  const setLoading = useSetAtom(atoms.loadingAtom);
  const setError = useSetAtom(atoms.errorAtom);

  const deleteInstance = useCallback(async (): Promise<void> => {
    const user = auth.currentUser;

    // Set loading state
    setLoading((prev) => ({ ...prev, delete: true }));

    // Clear error state
    setError(null);

    try {
      if (!user) return;

      const idToken = await user.getIdToken();
      const response = await fetch('/api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ action: 'delete', tier }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete instance: ${response.status}`);
      }

      // Update instance data to null since it's deleted
      setInstance(null);

      toast.success('Instance has been terminated');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      // Clear loading state
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  }, [tier, setInstance, setLoading, setError]);

  return {
    deleteInstance,
  };
};
